import { useRef } from 'react';
import { MatchCard } from './match-card';
import { BracketConnectors, type ConnectorLink } from './bracket-connectors';
import {
  computeBracketLayout,
  BRACKET_CARD_H,
  BRACKET_COL_W,
  BRACKET_COL_GAP,
} from './bracket-layout';
import { roundProgressLabel } from '@/lib/match-format';
import type { Match } from '@/data/types';

interface Props {
  matches: Match[];
  onSelectMatch?: (m: Match) => void;
  placeByParticipant?: Map<string, string>;
}

export function DoubleElimBracket({ matches, onSelectMatch, placeByParticipant }: Props) {
  const upper = matches.filter((m) => m.stage === 'MAIN' && m.id !== 'DE-GF');
  const lower = matches.filter((m) => m.stage === 'LOWER');
  const gf = matches.find((m) => m.id === 'DE-GF');

  return (
    <div className="space-y-10">
      <section>
        <div className="t-eyebrow" style={{ marginBottom: 8 }}>Верхняя сетка</div>
        <BracketColumns
          matches={upper}
          onSelectMatch={onSelectMatch}
          placeByParticipant={placeByParticipant}
        />
      </section>

      <section>
        <div className="t-eyebrow" style={{ marginBottom: 8 }}>Нижняя сетка</div>
        <BracketColumns
          matches={lower}
          onSelectMatch={onSelectMatch}
          placeByParticipant={placeByParticipant}
        />
      </section>

      {gf && (
        <section>
          <div className="t-eyebrow" style={{ marginBottom: 8 }}>Гранд-финал</div>
          <MatchCard
            match={gf}
            onClick={() => onSelectMatch?.(gf)}
            placeByParticipant={placeByParticipant}
          />
        </section>
      )}
    </div>
  );
}

function BracketColumns({
  matches,
  onSelectMatch,
  placeByParticipant,
}: {
  matches: Match[];
  onSelectMatch?: (m: Match) => void;
  placeByParticipant?: Map<string, string>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Связи строим только внутри текущей сетки (nextMatchId). Межсеточные
  // переходы (loserNextMatchId) намеренно не рисуем — они визуально путают.
  const ids = new Set(matches.map((m) => m.id));
  const links: ConnectorLink[] = matches
    .filter((m) => m.nextMatchId && ids.has(m.nextMatchId))
    .map((m) => ({ fromId: m.id, toId: m.nextMatchId as string }));

  const layout = computeBracketLayout(matches);

  return (
    <div ref={containerRef} className="bracket-scroll" style={{ position: 'relative' }}>
      <BracketConnectors containerRef={containerRef} links={links} />
      <div className="relative" style={{ width: layout.width, height: layout.height }}>
        {layout.rounds.map((round, roundIdx) => {
          const inRound = matches.filter((m) => m.round === round);
          const left = roundIdx * (BRACKET_COL_W + BRACKET_COL_GAP);
          return (
            <div
              key={round}
              className="absolute bracket-col-head"
              style={{ left, top: 0, width: BRACKET_COL_W }}
            >
              Раунд {round} <span className="count">{roundProgressLabel(inRound)}</span>
            </div>
          );
        })}
        {layout.positioned.map(({ match, left, top }) => (
          <div
            key={match.id}
            data-match-id={match.id}
            className="absolute"
            style={{ left, top, width: BRACKET_COL_W, height: BRACKET_CARD_H }}
          >
            <MatchCard
              match={match}
              onClick={() => onSelectMatch?.(match)}
              placeByParticipant={placeByParticipant}
              fill
            />
          </div>
        ))}
      </div>
    </div>
  );
}
